import {
  Button,
  DatePicker,
  Popover,
  Spinner,
  TextStyle,
} from '@shopify/polaris';
import { ShipDate } from '../../models/shipDate';
import React, { useEffect, useState } from 'react';

type DeliveryDateProps = {
  shipDateObject: ShipDate;
  allowSend: boolean;
  saveDateHandler: (date: ShipDate) => Promise<void> | void;
  sendCustomerEmailHandler: (date: Date, sentTime: number) => Promise<{email: string}>;
  emailConfirmed: boolean;
};

const DeliveryDate = ({
  shipDateObject,
  allowSend,
  saveDateHandler,
  sendCustomerEmailHandler,
  emailConfirmed,
}: DeliveryDateProps) => {
  const { date, sent }: ShipDate = {
    date: shipDateObject?.date ?? new Date(),
    sent: shipDateObject?.sent ?? {},
  };

  const [selectedDate, setSelectedDate] = useState(date);
  const [datePicker, setDatePicker] = useState({
    opened: false,
    month: selectedDate.getMonth(),
    year: selectedDate.getFullYear(),
  });

  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (
      allowSend &&
      emailConfirmed &&
      !!shipDateObject &&
      !!shipDateObject.date &&
      !shipDateObject.sent &&
      !sending
    ) {
      onSaveBtnClicked();
    }
  }, [allowSend, emailConfirmed, shipDateObject]);

  async function onSaveBtnClicked() {
    setSaving(true);
    let newShipDateObject: ShipDate = { date: selectedDate };
    if (allowSend && emailConfirmed) {
      newShipDateObject = await sendCustomerEmail();
    }
    await saveDateHandler(newShipDateObject);
    setSaving(false);
  }

  async function sendCustomerEmail() {
    setSending(true);
    try {
      const res = await sendCustomerEmailHandler(selectedDate, sent.time);
      setSending(false);
      return {
        date: selectedDate,
        sent: {
          time: Date.now(),
          email: res.email,
        },
      };
    } catch (e) {
      setSending(false);
      return {};
    }
  }

  function toggleDatePickerOpened() {
    setDatePicker({
      ...datePicker,
      opened: !datePicker.opened,
    });
  }

  function updateDatePickerMonthView(month: number, year: number) {
    setDatePicker({ ...datePicker, month, year });
  }

  function updateSelectedDate(value: { start: Date; end: Date }) {
    // value is { start: Date; end: Date }
    setSelectedDate(new Date(value.start));
  }

  function selectedDateIsSaved() {
    return (
      !!shipDateObject?.date &&
      JSON.stringify(selectedDate) === JSON.stringify(shipDateObject.date)
    );
  }

  return (
    <div style={{ display: 'flex' }}>
      {sending ? (
        <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
          <div style={{ marginRight: 5 }}>Sending email to customer...</div>
          <Spinner size="small" />
        </div>
      ) : null}

      <Popover
        activator={
          <Button
            disclosure={datePicker.opened ? 'up' : 'down'}
            onClick={toggleDatePickerOpened}
          >
            Delivery date
          </Button>
        }
        active={datePicker.opened}
        onClose={() => {}}
        fullHeight
      >
        {sent.time && sent.email && (
          <div style={{ padding: 10 }}>
            <p>Estimated date: {date.toLocaleDateString()}</p>
            <div style={{ marginTop: 10 }}>
              <p>
                Sent email to <i>{sent.email}</i>
              </p>
              <p>
                {new Date(sent.time).toLocaleDateString()} at{' '}
                {new Date(sent.time).toLocaleTimeString()}
              </p>
            </div>
          </div>
        )}
        <div style={{ padding: 10 }}>
          <DatePicker
            month={datePicker.month}
            year={datePicker.year}
            selected={selectedDate}
            onMonthChange={updateDatePickerMonthView}
            onChange={updateSelectedDate}
          />
          <div
            style={{
              marginTop: 10,
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {saving || sending ? (
              <Spinner size="small" />
            ) : selectedDateIsSaved() ? (
              <Button fullWidth disabled>
                Saved
              </Button>
            ) : (
              <Button fullWidth onClick={onSaveBtnClicked}>
                {sent.time && sent.email ? 'Change' : 'Save'}{' '}
                {allowSend && emailConfirmed ? '& Email customer' : ''}
              </Button>
            )}
          </div>
          {!emailConfirmed ? (
            <div
              style={{
                marginTop: 10,
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <TextStyle variation="subdued">
                Cannot notify customer as email was not provided
              </TextStyle>
            </div>
          ) : null}
        </div>
      </Popover>
    </div>
  );
};

export default DeliveryDate;
